/*
 * Obermaier Theme — core.js
 * Shared runtime for all components: config, events, cart API, section rendering,
 * sheets (native <dialog>), scroll reveal, announcements. No framework, no theme-vendor code.
 */

export const config = window.Obermaier || { routes: {}, strings: {}, moneyFormat: '{{amount_with_comma_separator}} €' };

/* ---------------------------------------------------------------- events */
// Theme-wide events are dispatched on document so any component (or app) can listen.
//   om:cart:updated   detail: { cart, source }
//   om:variant:change detail: { productId, sectionId, variant }
//   om:cart:error     detail: { message, source }
export function emit(name, detail = {}) {
  document.dispatchEvent(new CustomEvent(name, { detail }));
}
export function on(name, handler, options) {
  document.addEventListener(name, handler, options);
  return () => document.removeEventListener(name, handler, options);
}

/* ----------------------------------------------------------------- money */
export function formatMoney(cents, format = config.moneyFormat) {
  if (typeof cents === 'string') cents = cents.replace('.', '');
  const value = Number(cents) / 100;
  const fmt = (n, decimals, thousands = '.', decimal = ',') => {
    const fixed = n.toFixed(decimals);
    const [int, frac] = fixed.split('.');
    return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '');
  };
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount': return fmt(value, 2, ',', '.');
      case 'amount_no_decimals': return fmt(value, 0, ',', '.');
      case 'amount_with_comma_separator': return fmt(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator': return fmt(value, 0, '.', ',');
      case 'amount_with_apostrophe_separator': return fmt(value, 2, "'", '.');
      case 'amount_with_space_separator': return fmt(value, 2, ' ', ',');
      default: return fmt(value, 2, '.', ',');
    }
  });
}

export function t(key, vars = {}) {
  let s = key.split('.').reduce((o, k) => (o ? o[k] : undefined), config.strings) ?? key;
  for (const [k, v] of Object.entries(vars)) s = s.replaceAll(`{{ ${k} }}`, v).replaceAll(`{{${k}}}`, v);
  return s;
}

/* -------------------------------------------------------------- fetching */
const parser = new DOMParser();
export function parseHTML(html) {
  return parser.parseFromString(html, 'text/html');
}

/** Fetch one or more sections rendered for a URL (Section Rendering API). */
export async function fetchSections(url, sectionIds, { signal } = {}) {
  const u = new URL(url, location.origin);
  u.searchParams.set('sections', [].concat(sectionIds).join(','));
  const res = await fetch(u, { signal });
  if (!res.ok) throw new Error(`Section request failed (${res.status})`);
  return res.json();
}

/** Fetch a single section's HTML for a URL and return its parsed root element. */
export async function fetchSection(url, sectionId, { signal } = {}) {
  const u = new URL(url, location.origin);
  u.searchParams.set('section_id', sectionId);
  const res = await fetch(u, { signal });
  if (!res.ok) throw new Error(`Section request failed (${res.status})`);
  const doc = parseHTML(await res.text());
  return doc.querySelector(`[id="shopify-section-${sectionId}"]`) || doc.body.firstElementChild;
}

/** Replace the inner content of matching elements with the same element from a new document. */
export function swapFrom(doc, selector, root = document) {
  const fresh = doc.querySelector(selector);
  const current = root.querySelector(selector);
  if (fresh && current) current.replaceChildren(...fresh.childNodes);
  return current;
}

/* ------------------------------------------------------------------ cart */
const cartSectionIds = () =>
  [...document.querySelectorAll('[data-cart-section]')].map((el) => el.dataset.cartSection).filter(Boolean);

async function cartRequest(path, body) {
  const sections = [...new Set(cartSectionIds())];
  const payload = { ...body };
  if (sections.length) {
    payload.sections = sections.join(',');
    payload.sections_url = location.pathname;
  }
  const res = await fetch(`${config.routes.root || '/'}${path}`.replace('//', '/'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.status) {
    const message = data.description || data.message || t('cart.error');
    const error = new Error(message);
    error.status = res.status;
    throw error;
  }
  return data;
}

export const cart = {
  /** items: [{ id, quantity, properties?, selling_plan? }] */
  async add(items, { source = 'unknown' } = {}) {
    try {
      const data = await cartRequest('cart/add.js', { items });
      const state = await cart.get();
      renderCartSections(data.sections);
      emit('om:cart:updated', { cart: state, added: data.items || [data], source });
      return { added: data.items || [data], cart: state };
    } catch (error) {
      emit('om:cart:error', { message: error.message, source });
      throw error;
    }
  },
  async change(line, quantity, { source = 'unknown' } = {}) {
    try {
      const data = await cartRequest('cart/change.js', { line, quantity });
      renderCartSections(data.sections);
      emit('om:cart:updated', { cart: data, source });
      return data;
    } catch (error) {
      emit('om:cart:error', { message: error.message, source });
      throw error;
    }
  },
  async update(updates, { source = 'unknown', note } = {}) {
    const body = { updates };
    if (note !== undefined) body.note = note;
    const data = await cartRequest('cart/update.js', body);
    renderCartSections(data.sections);
    emit('om:cart:updated', { cart: data, source });
    return data;
  },
  async get() {
    const res = await fetch(`${config.routes.cart || '/cart'}.js`, { headers: { Accept: 'application/json' } });
    return res.json();
  },
};

export function renderCartSections(sections) {
  if (!sections) return;
  for (const [id, html] of Object.entries(sections)) {
    if (!html) continue;
    const doc = parseHTML(html);
    document.querySelectorAll(`[data-cart-section="${id}"]`).forEach((target) => {
      const fresh = doc.querySelector(`[data-cart-section="${id}"]`);
      if (fresh) target.replaceChildren(...fresh.childNodes);
    });
  }
}

/* ---------------------------------------------------------- live region */
let toastTimer;
export function announce(message, { tone = 'polite', visual = false, error = false } = {}) {
  const region = document.getElementById(tone === 'assertive' ? 'om-live-assertive' : 'om-live-polite');
  if (region) {
    region.textContent = '';
    requestAnimationFrame(() => (region.textContent = message));
  }
  if (!visual) return;
  let toast = document.getElementById('om-toast');
  if (!toast) {
    toast = Object.assign(document.createElement('div'), { id: 'om-toast', className: 'om-toast' });
    toast.setAttribute('aria-hidden', 'true');
    document.body.append(toast);
  }
  toast.textContent = message;
  toast.classList.toggle('om-toast--error', error);
  toast.classList.add('is-visible');
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 3200);
}

/* ---------------------------------------------------------------- sheets */
// Any element with [data-sheet-open="dialog-id"] opens that <dialog class="om-sheet">.
// [data-sheet-close] inside closes it. Clicking the backdrop closes. Focus returns to the opener.
const openers = new WeakMap();

/** Sheets may ship their content in <template data-sheet-content> (sections/header.liquid: mobile menu),
 *  so it does not weigh on every page load. It is added to the page once, before the sheet opens. */
export function stampSheet(dialog) {
  dialog?.querySelectorAll('template[data-sheet-content]').forEach((tpl) => tpl.replaceWith(document.importNode(tpl.content, true)));
}

/** Cart and search load their CSS without blocking the page ([data-sheet-css]); a very early tap waits for it. */
function sheetCssPending(id) {
  const link = document.querySelector(`link[data-sheet-css="${id}"]`);
  if (!link || link.sheet) return null; // link.sheet is set once loaded, even while media is still 'print'
  return new Promise((done) => {
    link.addEventListener('load', done, { once: true });
    link.addEventListener('error', done, { once: true });
  });
}

export function openSheet(id, opener = document.activeElement) {
  const dialog = document.getElementById(id);
  if (!dialog || dialog.open) return dialog;
  const pending = sheetCssPending(id);
  if (pending) {
    pending.then(() => openSheet(id, opener));
    return dialog;
  }
  stampSheet(dialog);
  openers.set(dialog, opener);
  document.documentElement.classList.add('om-locked');
  dialog.showModal();
  dialog.dispatchEvent(new CustomEvent('om:sheet:open', { bubbles: true }));
  return dialog;
}

export function closeSheet(dialogOrId) {
  const dialog = typeof dialogOrId === 'string' ? document.getElementById(dialogOrId) : dialogOrId;
  if (!dialog?.open) return;
  dialog.close();
}

function onSheetClosed(event) {
  const dialog = event.target;
  if (!(dialog instanceof HTMLDialogElement) || !dialog.classList.contains('om-sheet')) return;
  if (!document.querySelector('dialog.om-sheet[open]')) document.documentElement.classList.remove('om-locked');
  const opener = openers.get(dialog);
  if (opener && document.contains(opener)) opener.focus({ preventScroll: true });
  dialog.dispatchEvent(new CustomEvent('om:sheet:close', { bubbles: true }));
}

document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-sheet-open]');
  if (opener) {
    event.preventDefault();
    openSheet(opener.dataset.sheetOpen, opener);
    return;
  }
  const closer = event.target.closest('[data-sheet-close]');
  if (closer) {
    closeSheet(closer.closest('dialog'));
    return;
  }
  // Backdrop click: the event target is the dialog itself when clicking outside its box.
  if (event.target instanceof HTMLDialogElement && event.target.classList.contains('om-sheet')) {
    const r = event.target.getBoundingClientRect();
    const inside = event.clientX >= r.left && event.clientX <= r.right && event.clientY >= r.top && event.clientY <= r.bottom;
    if (!inside) closeSheet(event.target);
  }
});
document.addEventListener('close', onSheetClosed, true);
// Prepare deferred sheet content as soon as a pointer goes down on an opener (hides the cost of the tap).
// On iOS, haptics.js covers actions with a sibling span[data-om-haptic-surface]; resolve it back to its action.
document.addEventListener('pointerdown', (event) => {
  let el = event.target;
  if (el?.hasAttribute?.('data-om-haptic-surface')) el = el.previousElementSibling;
  const opener = el?.closest?.('[data-sheet-open]');
  if (opener) stampSheet(document.getElementById(opener.dataset.sheetOpen));
}, { capture: true, passive: true });

/* ---------------------------------------------------------- scroll reveal */
const motionOK = () => !matchMedia('(prefers-reduced-motion: reduce)').matches;

export function initReveal(root = document) {
  if (!document.documentElement.classList.contains('om-reveal-armed')) return;
  const items = root.querySelectorAll('[data-reveal]:not(.is-revealed)');
  if (!items.length) return;
  if (!('IntersectionObserver' in window) || !motionOK()) {
    items.forEach((el) => el.classList.add('is-revealed'));
    return;
  }
  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-revealed');
        io.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
  );
  items.forEach((el) => io.observe(el));
}

/* -------------------------------------------------------------- helpers */
export function debounce(fn, wait = 200) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
}

/** Lazy-define a custom element once. */
export function define(name, ctor) {
  if (!customElements.get(name)) customElements.define(name, ctor);
}

/** Load a theme module on first interaction or visibility, never blocking first paint. */
export function whenVisible(el, cb, rootMargin = '200px') {
  if (!('IntersectionObserver' in window)) return cb();
  const io = new IntersectionObserver((entries) => {
    if (entries.some((e) => e.isIntersecting)) {
      io.disconnect();
      cb();
    }
  }, { rootMargin });
  io.observe(el);
}

/* ------------------------------------------------------------ consent */
// Convenience storage (recent searches, recently viewed) needs consent to preferences (§ 25 TDDDG).
// Shopify's privacy API loads lazily, so it is requested first; without it (no cookie banner configured)
// storage stays allowed.
let consentApi;
function loadConsentApi() {
  if (consentApi) return consentApi;
  consentApi = new Promise((resolve) => {
    // The banner script may create a bare placeholder object first; only a complete API counts.
    if (typeof window.Shopify?.customerPrivacy?.preferencesProcessingAllowed === 'function') return resolve(window.Shopify.customerPrivacy);
    if (typeof window.Shopify?.loadFeatures !== 'function') return resolve(null);
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) =>
      resolve(error ? null : window.Shopify.customerPrivacy || null),
    );
  });
  return consentApi;
}
export async function preferencesAllowed() {
  const privacy = await loadConsentApi();
  if (!privacy || typeof privacy.preferencesProcessingAllowed !== 'function') return true;
  try {
    return privacy.preferencesProcessingAllowed();
  } catch {
    return false;
  }
}

// Without consent to preferences nothing stays in this browser's history lists (§ 25 TDDDG): on every page
// load and whenever the visitor changes the cookie choice. Components listen to om:preferences:revoked.
const PREFERENCE_KEYS = ['om:recently-viewed', 'om:recent-searches'];
async function dropPreferenceStorage() {
  let stored = false;
  try {
    stored = PREFERENCE_KEYS.some((key) => localStorage.getItem(key) !== null);
  } catch {
    return;
  }
  if (!stored || (await preferencesAllowed())) return;
  try {
    PREFERENCE_KEYS.forEach((key) => localStorage.removeItem(key));
  } catch {}
  emit('om:preferences:revoked');
}

/* --------------------------------------------------------------- boot */
dropPreferenceStorage();
document.addEventListener('visitorConsentCollected', dropPreferenceStorage);
if (window.Obermaier) window.Obermaier.coreReady = true;
initReveal();
document.addEventListener('shopify:section:load', (e) => initReveal(e.target));

// Free-shipping hairline (sections/ship-meter): its label shows briefly after each cart change and while
// a bag link in the header is hovered.
let shiplineTimer;
function peekShipline(on, ms = 0) {
  const line = document.querySelector('[data-shipline]');
  if (!line) return;
  clearTimeout(shiplineTimer);
  line.classList.toggle('is-peek', on);
  if (on && ms) shiplineTimer = setTimeout(() => line.classList.remove('is-peek'), ms);
}
on('om:cart:updated', () => requestAnimationFrame(() => peekShipline(true, 4500)));
document.addEventListener('pointerover', (event) => {
  if (event.pointerType === 'mouse' && event.target.closest?.('.om-header [data-cart-open]')) peekShipline(true);
});
document.addEventListener('pointerout', (event) => {
  const from = event.target.closest?.('.om-header [data-cart-open]');
  if (from && !from.contains(event.relatedTarget)) peekShipline(true, 900);
});

// Cart count badges follow every cart change, regardless of which component caused it.
on('om:cart:updated', ({ detail }) => {
  const count = detail.cart?.item_count;
  if (typeof count !== 'number') return;
  document.querySelectorAll('[data-cart-count]').forEach((el) => {
    el.textContent = count > 0 ? String(count) : '';
    el.dataset.count = String(count);
  });
  const label = count > 0 ? `, ${t(count === 1 ? 'cart.items_one' : 'cart.items_other').replace('COUNT', String(count))}` : '';
  document.querySelectorAll('[data-cart-count-label]').forEach((el) => (el.textContent = label));
});
